import React, { useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { DS } from '../ds';
import { useAuth } from '../../contexts/AuthContext';
import { BlogLoginModal } from '../BlogLoginModal';
interface BlogArticle {
  id: number;
  title: string;
  category: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  readTime: string;
}
interface FAQ {
  question: string;
  answer: string;
}
interface BlogPageProps {
  onBack: () => void;
}
const faqs: FAQ[] = [{
  question: '¿Qué son los alineadores invisibles?',
  answer: 'Los alineadores invisibles son dispositivos transparentes y removibles que corrigen la posición de los dientes de forma discreta y eficaz. Funcionan mediante una serie de alineadores progresivos que aplican presión controlada, moviendo los dientes gradualmente hacia su posición correcta.'
}, {
  question: '¿Cuánto tiempo tarda el tratamiento?',
  answer: 'El tiempo depende de la complejidad del caso, pero generalmente va entre 6 y 18 meses. Cada alineador se utiliza durante 1 a 2 semanas antes de cambiar al siguiente.'
}, {
  question: '¿Qué criterios debe cumplir un caso para ser viable?',
  answer: 'Casos ideales son maloclusiones leves a moderadas, apiñamiento dental moderado, espacios entre dientes, mordida cruzada leve y correcciones periodontales. Casos no recomendados incluyen maloclusiones severas o déficit severo de hueso alveolar.'
}, {
  question: '¿Cuál es el proceso de envío de casos?',
  answer: 'El proceso es sencillo: envías archivos STL, modelos 3D o escaneos intraorales. Nuestro equipo valida la viabilidad, realiza la planeación digital, te la presenta para aprobación, y finalmente fabricamos los alineadores con entrega eco-premium.'
}, {
  question: '¿Qué materiales se utilizan?',
  answer: 'Usamos materiales de grado médico aprobados por INVIMA para garantizar seguridad y eficacia. Todos nuestros alineadores cumplen con estándares clínicos internacionales.'
}];
const articles: BlogArticle[] = [{
  id: 1,
  title: '¿Qué son los alineadores invisibles y cómo funcionan?',
  category: 'Fundamentos',
  excerpt: 'Descubre la tecnología detrás de los alineadores invisibles y por qué son la solución de ortodoncia moderna.',
  content: `Los alineadores invisibles son dispositivos transparentes y removibles que corrigen la posición de los dientes de forma discreta y eficaz.

Funcionan mediante una serie de alineadores progresivos que aplican presión controlada a los dientes, moviéndolos gradualmente hacia su posición correcta. Cada alineador se utiliza durante 1 a 2 semanas antes de cambiar al siguiente.

Beneficios principales
• Transparencia: prácticamente invisibles.
• Comodidad: removibles para comer e higiene.
• Precisión: planeación digital 3D.
• Eficacia: resultados predecibles.

La tecnología utiliza escaneo 3D y software avanzado de modelado para crear un plan de tratamiento personalizado antes de fabricar los alineadores.`,
  author: 'Dra. María Rodríguez',
  date: '15 de enero, 2026',
  readTime: '5 min'
}, {
  id: 2,
  title: 'Proceso clínico paso a paso: envío, validación y fabricación',
  category: 'Proceso',
  excerpt: 'Conoce cada etapa del proceso clínico de Alinova, desde el envío del caso hasta la entrega final.',
  content: `Nuestro proceso es simple, transparente y pensado para que el profesional mantenga el control total.

Paso 1 · Envía el caso
Envías archivos STL, modelos 3D o escaneos intraorales a través de nuestro sistema.

Paso 2 · Análisis de validez
Nuestro equipo de especialistas revisa si el caso es viable para alineadores invisibles.

Paso 3 · Planeación digital
Creamos un plan de tratamiento detallado con simulación 3D del movimiento dental.

Paso 4 · Validación profesional
Tú apruebas el plan antes de que iniciemos la fabricación.

Paso 5 · Fabricación precisa
Fabricamos los alineadores con tecnología de punta y precisión submilimétrica.

Paso 6 · Entrega eco-premium
Recibes el kit completo en empaque eco-friendly con tu marca.

Tiempo total: 10 a 15 días hábiles desde la aprobación del plan.`,
  author: 'Dr. Carlos Méndez',
  date: '10 de enero, 2026',
  readTime: '7 min'
}, {
  id: 3,
  title: 'Planeación digital 3D: precisión en cada movimiento',
  category: 'Técnica',
  excerpt: 'Aprende cómo la tecnología 3D garantiza precisión clínica en cada movimiento dental.',
  content: `La planeación digital 3D es el corazón de nuestro proceso.

Captura digital
Utilizamos escaneos 3D intraorales de alta resolución que capturan cada detalle de la dentición.

Análisis biomecánico
El software analiza la anatomía dental y determina la trayectoria óptima para cada diente.

Simulación predictiva
Antes de fabricar, simulamos todo el movimiento del tratamiento. Ves exactamente qué va a pasar.

Precisión submilimétrica
Cada alineador se fabrica con tolerancias de micras para garantizar eficacia.

Control profesional
Revisas el plan digital y lo apruebas antes de la fabricación. Total transparencia.

Beneficios: resultados predecibles, mayor eficacia clínica, menor tiempo de tratamiento y mayor satisfacción del paciente.`,
  author: 'Ing. Roberto Salazar',
  date: '5 de enero, 2026',
  readTime: '8 min'
}, {
  id: 4,
  title: 'Tendencias en ortodoncia invisible 2026',
  category: 'Tendencias',
  excerpt: 'Descubre qué está cambiando en la industria de la ortodoncia invisible este año.',
  content: `La ortodoncia invisible evoluciona constantemente. Estas son las tendencias principales.

1 · Mayor precisión mediante IA
Los algoritmos están mejorando la predicción de movimientos dentales.

2 · Materiales más cómodos
Nuevas composiciones de polímeros ofrecen mayor flexibilidad y comodidad.

3 · Tratamientos más rápidos
Innovaciones en fabricación permiten tratamientos entre 20 % y 30 % más rápidos.

4 · Monitoreo remoto
Sistemas de seguimiento permiten controlar el avance del tratamiento a distancia.

5 · Sostenibilidad
Mayor enfoque en empaque eco-friendly y procesos de fabricación sostenibles.

6 · Casos más complejos
La tecnología permite tratar casos que antes requerían brackets tradicionales.`,
  author: 'Dr. Carlos Méndez',
  date: '20 de diciembre, 2025',
  readTime: '6 min'
}];
const categories = ['Todos', ...Array.from(new Set(articles.map(a => a.category)))];
export const BlogPage = ({
  onBack
}: BlogPageProps) => {
  const {
    isVerified
  } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'faq' | 'articles'>('faq');
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [selected, setSelected] = useState<BlogArticle | null>(null);
  const filteredArticles = activeCategory === 'Todos' ? articles : articles.filter(a => a.category === activeCategory);
  const backButton = (label: string, onClick: () => void) => <button type="button" onClick={onClick} style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'transparent',
    border: 'none',
    padding: 0,
    color: DS.color.teal,
    fontFamily: DS.font.body,
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer'
  }} data-id="element-242" data-mp="3n2uh9ovp907e">
      <ArrowLeftIcon size={16} aria-hidden="true" data-id="element-243" data-mp="34b1kdjdieuue" />
      {label}
    </button>;

  // ---- Artículo completo ----
  if (selected) {
    return <section id="blog" style={{
      background: '#fff',
      minHeight: '100vh',
      paddingTop: '68px'
    }} data-id="element-244" data-mp="3944js84ywyii">
        <div style={{
        padding: '20px 24px',
        borderBottom: `1px solid ${DS.color.border}`
      }} data-id="element-245" data-mp="2zc897xlp3fxe">
          <div style={{
          maxWidth: '860px',
          margin: '0 auto'
        }} data-id="element-246" data-mp="2lds0y286phd5">
            {backButton('Volver al blog', () => setSelected(null))}
          </div>
        </div>

        <article style={{
        padding: '56px 24px 80px'
      }} data-id="element-247" data-mp="35tbglbl8h8br">
          <div style={{
          maxWidth: '760px',
          margin: '0 auto'
        }} data-id="element-248" data-mp="35ss4ibi9le5n">
            <div style={{
            display: 'flex',
            gap: '14px',
            alignItems: 'center',
            flexWrap: 'wrap',
            fontFamily: DS.font.body,
            fontSize: '12px',
            color: DS.color.muted,
            marginBottom: '20px'
          }} data-id="element-249" data-mp="30vi82bwgk28b">
              <span style={{
              background: DS.color.mint,
              color: DS.color.tealDeep,
              padding: '4px 10px',
              borderRadius: '8px',
              fontWeight: 700
            }} data-id="element-250" data-mp="2i3z688e66u7v">
                {selected.category}
              </span>
              <span data-id="element-251" data-mp="243687donth00">{selected.date}</span>
              <span data-id="element-252" data-mp="246aj0twmrazn">{selected.readTime} de lectura</span>
            </div>

            <h1 style={{
            fontFamily: DS.font.heading,
            fontSize: 'clamp(2rem, 4.5vw, 2.8rem)',
            fontWeight: 700,
            color: DS.color.ink,
            lineHeight: 1.18,
            letterSpacing: '-0.025em',
            margin: '0 0 24px'
          }} data-id="element-253" data-mp="1z5mx2mn9dkca">
              {selected.title}
            </h1>

            <div style={{
            paddingBottom: '28px',
            borderBottom: `1px solid ${DS.color.border}`,
            marginBottom: '32px',
            fontFamily: DS.font.body,
            fontSize: '14px'
          }} data-id="element-254" data-mp="smbsf8vkast1">
              <strong style={{
              color: DS.color.ink
            }} data-id="element-255" data-mp="4whfysf5n69b">{selected.author}</strong>
              <div style={{
              color: DS.color.muted
            }} data-id="element-256" data-mp="9o9651uef9yx">
                Especialista en ortodoncia
              </div>
            </div>

            <div style={{
            fontFamily: DS.font.body,
            fontSize: '16px',
            lineHeight: 1.8,
            color: DS.color.body,
            whiteSpace: 'pre-wrap'
          }} data-id="element-257" data-mp="38dptj02unjy8">
              {selected.content}
            </div>

            <div style={{
            marginTop: '56px',
            padding: '32px',
            background: 'rgba(0, 170, 212, 0.08)',
            border: `1px solid ${DS.color.aqua}30`,
            borderRadius: DS.radius.cardLg,
            textAlign: 'center'
          }} data-id="element-258" data-mp="17zpki4ussghp">
              <h2 style={{
              fontFamily: DS.font.heading,
              fontSize: '20px',
              fontWeight: 700,
              color: DS.color.ink,
              margin: '0 0 10px'
            }} data-id="element-259" data-mp="p6vbrsnrpklx">
                ¿Tienes una pregunta específica?
              </h2>
              <p style={{
              fontFamily: DS.font.body,
              fontSize: '15px',
              color: DS.color.body,
              margin: '0 0 22px'
            }} data-id="element-260" data-mp="1h137nahei7h">
                Escríbele a nuestro equipo de especialistas por WhatsApp.
              </p>
              <a href={DS.wa} target="_blank" rel="noopener noreferrer" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '13px 28px',
              borderRadius: DS.radius.pill,
              background: '#00AAD4',
              boxShadow: '0 8px 26px rgba(0, 170, 212, 0.42)',
              color: '#fff',
              fontFamily: DS.font.body,
              fontSize: '14px',
              fontWeight: 700,
              textDecoration: 'none',
              transition: `background-color 0.22s ${DS.ease.smooth}, box-shadow 0.22s ${DS.ease.smooth}`
            }} onMouseEnter={e => {
              e.currentTarget.style.background = '#0099CC';
              e.currentTarget.style.boxShadow = '0 12px 34px rgba(0, 170, 212, 0.55)';
            }} onMouseLeave={e => {
              e.currentTarget.style.background = '#00AAD4';
              e.currentTarget.style.boxShadow = '0 8px 26px rgba(0, 170, 212, 0.42)';
            }} data-id="element-261" data-mp="3q1cozj5bkk3x">
                Enviar consulta
                <ArrowRightIcon size={15} aria-hidden="true" data-id="element-262" data-mp="2xhqynfuy8pgn" />
              </a>
            </div>
          </div>
        </article>
      </section>;
  }
  return <section id="blog" style={{
    background: '#fff',
    minHeight: '100vh',
    paddingTop: '68px'
  }} data-id="element-263" data-mp="2nrxn2eut4ace">
      {/* Hero */}
      <div style={{
      padding: '56px 24px',
      background: 'linear-gradient(135deg, rgba(0,170,212,0.08) 0%, rgba(45,212,212,0.05) 100%)',
      borderBottom: `1px solid ${DS.color.border}`
    }} data-id="element-264" data-mp="2soebla5x4qq0">
        <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }} data-id="element-265" data-mp="3uktsv5fb62tu">
          <div style={{
          marginBottom: '24px'
        }} data-id="element-266" data-mp="3uj8xjdl66fux">
            {backButton('Volver al inicio', onBack)}
          </div>

          <h1 style={{
          fontFamily: DS.font.heading,
          fontSize: 'clamp(2.2rem, 5vw, 3rem)',
          fontWeight: 700,
          color: DS.color.ink,
          letterSpacing: '-0.03em',
          lineHeight: 1.1,
          margin: '0 0 14px'
        }} data-id="element-267" data-mp="85r6ajqhjdmj">
            Blog de Alinova
          </h1>
          <p style={{
          fontFamily: DS.font.body,
          fontSize: '17px',
          color: DS.color.body,
          maxWidth: '620px',
          margin: 0
        }} data-id="element-268" data-mp="37mp9z3n9ogrb">
            Ortodoncia invisible, tendencias clínicas y procesos de precisión
            para optimizar tus tratamientos.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
      padding: '0 24px',
      borderBottom: `1px solid ${DS.color.border}`
    }} data-id="element-269" data-mp="vxftxmsd3dx5">
        <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        gap: '28px'
      }} data-id="element-270" data-mp="36i6xdjxgooli">
          {[{
          id: 'faq' as const,
          label: 'Preguntas frecuentes'
        }, {
          id: 'articles' as const,
          label: isVerified ? 'Artículos' : 'Artículos (requiere verificación)'
        }].map(tab => {
          const isActive = activeTab === tab.id;
          return <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} aria-current={isActive ? 'page' : undefined} style={{
            padding: '18px 0',
            background: 'transparent',
            border: 'none',
            color: isActive ? DS.color.teal : DS.color.body,
            fontFamily: DS.font.body,
            fontSize: '14px',
            fontWeight: 700,
            cursor: 'pointer',
            borderBottom: isActive ? `3px solid ${DS.color.aqua}` : '3px solid transparent',
            transition: `color 0.25s ${DS.ease.smooth}, border-color 0.25s ${DS.ease.smooth}`
          }} data-id="element-271" data-mp="3205eaoesfsk">
                {tab.label}
              </button>;
        })}
        </div>
      </div>

      {/* Contenido */}
      <div style={{
      padding: '56px 24px 80px'
    }} data-id="element-272" data-mp="26hlkbdwbuh4y">
        <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }} data-id="element-273" data-mp="21k1xvqph6y1x">
          {activeTab === 'faq' && <div data-id="element-274" data-mp="2yyxp5u0oyvy1">
              <h2 style={{
            fontFamily: DS.font.heading,
            fontSize: '26px',
            fontWeight: 700,
            color: DS.color.ink,
            margin: '0 0 40px'
          }} data-id="element-275" data-mp="2yzfstbpoocfz">
                Preguntas frecuentes
              </h2>

              <div style={{
            maxWidth: '760px'
          }} data-id="element-276" data-mp="3i3ayr3ff423r">
                {faqs.map((faq, index) => <div key={faq.question} style={{
              marginBottom: '28px',
              paddingBottom: '28px',
              borderBottom: index < faqs.length - 1 ? `1px solid ${DS.color.border}` : 'none'
            }} data-id="element-277" data-mp="34bwd0ou4lmt0" data-mpi={index}>
                    <h3 style={{
                fontFamily: DS.font.heading,
                fontSize: '17px',
                fontWeight: 700,
                color: DS.color.tealDeep,
                margin: '0 0 10px'
              }} data-id="element-278" data-mp="2lf4mpvg627fg">
                      {faq.question}
                    </h3>
                    <p style={{
                fontFamily: DS.font.body,
                fontSize: '15px',
                color: DS.color.body,
                lineHeight: 1.65,
                margin: 0
              }} data-id="element-279" data-mp="286chv6p0y0eq">
                      {faq.answer}
                    </p>
                  </div>)}
              </div>
            </div>}

          {activeTab === 'articles' && <>
              {isVerified ? <>
                  <h2 style={{
              fontFamily: DS.font.heading,
              fontSize: '26px',
              fontWeight: 700,
              color: DS.color.ink,
              margin: '0 0 24px'
            }} data-id="element-280" data-mp="30uqepj4dmqop">
                    Artículos exclusivos
                  </h2>

                  <div style={{
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
              marginBottom: '36px'
            }} data-id="element-281" data-mp="glya0dzogp3d">
                    {categories.map(category => {
                const isActive = activeCategory === category;
                return <button key={category} type="button" onClick={() => setActiveCategory(category)} aria-pressed={isActive} style={{
                  padding: '9px 18px',
                  background: isActive ? DS.color.aqua : 'transparent',
                  color: isActive ? DS.color.tealDeep : DS.color.body,
                  border: `1.5px solid ${isActive ? DS.color.aqua : DS.color.border}`,
                  borderRadius: DS.radius.pill,
                  cursor: 'pointer',
                  fontFamily: DS.font.body,
                  fontSize: '14px',
                  fontWeight: isActive ? 700 : 500,
                  transition: `background-color 0.25s ${DS.ease.smooth}, border-color 0.25s ${DS.ease.smooth}`
                }} data-id="element-282" data-mp="3tsqac2e7hgsf">
                          {category}
                        </button>;
              })}
                  </div>

                  <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '28px'
            }} data-id="element-283" data-mp="2jwbehv8rejln">
                    {filteredArticles.map(article => <button key={article.id} type="button" onClick={() => setSelected(article)} style={{
                textAlign: 'left',
                padding: '24px',
                background: '#fff',
                border: `1px solid ${DS.color.border}`,
                borderRadius: DS.radius.cardLg,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                transition: `transform 0.3s ${DS.ease.smooth}, box-shadow 0.3s ${DS.ease.smooth}`
              }} onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 170, 212, 0.15)';
              }} onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }} data-id="element-284" data-mp="d1end3y3nkam">
                        <span style={{
                  width: 'fit-content',
                  padding: '4px 10px',
                  background: DS.color.mint,
                  color: DS.color.tealDeep,
                  borderRadius: '8px',
                  fontFamily: DS.font.body,
                  fontSize: '11px',
                  fontWeight: 700,
                  marginBottom: '14px'
                }} data-id="element-285" data-mp="3lyfq5zbsxdou">
                          {article.category}
                        </span>

                        <h3 style={{
                  fontFamily: DS.font.heading,
                  fontSize: '18px',
                  fontWeight: 700,
                  color: DS.color.ink,
                  lineHeight: 1.35,
                  margin: '0 0 10px'
                }} data-id="element-286" data-mp="mc91x0mgync8">
                          {article.title}
                        </h3>

                        <p style={{
                  fontFamily: DS.font.body,
                  fontSize: '14px',
                  color: DS.color.body,
                  lineHeight: 1.6,
                  margin: '0 0 20px'
                }} data-id="element-287" data-mp="2xajyl10uoyej">
                          {article.excerpt}
                        </p>

                        <div style={{
                  marginTop: 'auto',
                  borderTop: `1px solid ${DS.color.border}`,
                  paddingTop: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  fontFamily: DS.font.body,
                  fontSize: '12px',
                  color: DS.color.muted
                }} data-id="element-288" data-mp="29mjz9cph3zoc">
                          <span data-id="element-289" data-mp="3nngg1md0ng7h">
                            <strong style={{
                      color: DS.color.ink
                    }} data-id="element-290" data-mp="3nkao7zd06082">
                              {article.author}
                            </strong>{' '}
                            · {article.readTime}
                          </span>
                          <ArrowRightIcon size={16} color={DS.color.aqua} aria-hidden="true" data-id="element-291" data-mp="66yie3xzvo9b" />
                        </div>
                      </button>)}
                  </div>
                </> : <div style={{
            textAlign: 'center',
            padding: '64px 0'
          }} data-id="element-292" data-mp="2vwsbe0c4vsp2">
                  <h2 style={{
              fontFamily: DS.font.heading,
              fontSize: '26px',
              fontWeight: 700,
              color: DS.color.ink,
              margin: '0 0 12px'
            }} data-id="element-293" data-mp="3b5w5beozvvi5">
                    Acceso restringido
                  </h2>
                  <p style={{
              fontFamily: DS.font.body,
              fontSize: '15px',
              color: DS.color.body,
              margin: '0 0 28px'
            }} data-id="element-294" data-mp="3u4i42mx1haiw">
                    Verifica tu email profesional para acceder a los
                    artículos exclusivos.
                  </p>
                  <button type="button" onClick={() => setShowLoginModal(true)} style={{
              padding: '13px 28px',
              background: '#00AAD4',
              boxShadow: '0 8px 26px rgba(0, 170, 212, 0.42)',
              color: '#fff',
              border: 'none',
              borderRadius: DS.radius.pill,
              fontFamily: DS.font.body,
              fontSize: '14px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: `background-color 0.22s ${DS.ease.smooth}, box-shadow 0.22s ${DS.ease.smooth}`
            }} onMouseEnter={e => {
              e.currentTarget.style.background = '#0099CC';
              e.currentTarget.style.boxShadow = '0 12px 34px rgba(0, 170, 212, 0.55)';
            }} onMouseLeave={e => {
              e.currentTarget.style.background = '#00AAD4';
              e.currentTarget.style.boxShadow = '0 8px 26px rgba(0, 170, 212, 0.42)';
            }} data-id="element-295" data-mp="29ooctu9w1t85">
                    Verificar email
                  </button>
                </div>}
            </>}
        </div>
      </div>

      <BlogLoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} onLoginSuccess={() => setShowLoginModal(false)} data-id="element-296" data-mp="yv39h22bmqcy" />
    </section>;
};